/**
 * Utilitário para corrigir meta tags deprecated do Canva
 * Substitui meta tags Apple deprecated por meta tags padrão
 */

export interface MetaTagStatus {
  deprecated: string[];
  correct: string[];
  missing: string[];
  fixed: boolean;
}

export class MetaTagFixer {
  private static readonly DEPRECATED_TAGS = [
    'apple-mobile-web-app-capable',
    'apple-mobile-web-app-status-bar-style',
    'apple-mobile-web-app-title'
  ];

  private static readonly CORRECT_TAG = 'mobile-web-app-capable';

  /**
   * Verifica o status atual das meta tags
   */
  static checkStatus(): MetaTagStatus {
    const deprecated: string[] = [];
    const correct: string[] = [];
    const missing: string[] = [];

    // Verificar meta tags deprecated
    this.DEPRECATED_TAGS.forEach(tagName => {
      const tag = document.querySelector(`meta[name="${tagName}"]`);
      if (tag) {
        deprecated.push(tagName);
      }
    });

    // Verificar meta tag correta
    const correctTag = document.querySelector(`meta[name="${this.CORRECT_TAG}"]`);
    if (correctTag) {
      correct.push(this.CORRECT_TAG);
    } else {
      missing.push(this.CORRECT_TAG);
    }

    return {
      deprecated,
      correct,
      missing,
      fixed: deprecated.length === 0 && correct.length > 0
    };
  }

  /**
   * Corrige meta tags deprecated automaticamente
   */
  static fix(): MetaTagStatus {
    try {
      console.log("🔧 Iniciando correção de meta tags...");

      // Remover meta tags deprecated
      this.DEPRECATED_TAGS.forEach(tagName => {
        const tag = document.querySelector(`meta[name="${tagName}"]`);
        if (tag) {
          tag.remove();
          console.log(`✅ Meta tag deprecated '${tagName}' removida`);
        }
      });

      // Adicionar meta tag correta se não existir
      const correctTag = document.querySelector(`meta[name="${this.CORRECT_TAG}"]`);
      if (!correctTag) {
        const newMeta = document.createElement('meta');
        newMeta.name = this.CORRECT_TAG;
        newMeta.content = 'yes';
        document.head.appendChild(newMeta);
        console.log(`✅ Meta tag correta '${this.CORRECT_TAG}' adicionada`);
      } else {
        console.log(`✅ Meta tag '${this.CORRECT_TAG}' já existe`);
      }

      // Retornar status atualizado
      const status = this.checkStatus();
      status.fixed = true;
      
      console.log("✅ Correção de meta tags concluída");
      return status;

    } catch (error) {
      console.error("❌ Erro ao corrigir meta tags:", error);
      return this.checkStatus();
    }
  }

  /**
   * Executa correção automática se necessário
   */
  static autoFix(): MetaTagStatus {
    const status = this.checkStatus();
    
    if (status.deprecated.length > 0 || status.missing.length > 0) {
      console.log("⚠️ Meta tags deprecated detectadas, corrigindo automaticamente...");
      return this.fix();
    } else {
      console.log("✅ Meta tags já estão corretas");
      return status;
    }
  }

  /**
   * Adiciona listener para corrigir meta tags quando o DOM estiver pronto
   */
  static init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.autoFix();
      });
    } else {
      this.autoFix();
    }
  }

  /**
   * Verifica se há problemas com meta tags
   */
  static hasIssues(): boolean {
    const status = this.checkStatus();
    return status.deprecated.length > 0 || status.missing.length > 0;
  }

  /**
   * Retorna mensagem de status para o usuário
   */
  static getStatusMessage(): string {
    const status = this.checkStatus();
    
    if (status.fixed) {
      return "✅ Meta tags corrigidas com sucesso";
    } else if (status.deprecated.length > 0) {
      return `⚠️ ${status.deprecated.length} meta tag(s) deprecated encontrada(s)`;
    } else if (status.missing.length > 0) {
      return "⚠️ Meta tag correta não encontrada";
    } else {
      return "✅ Meta tags estão corretas";
    }
  }
}

// Auto-inicialização quando o módulo for carregado
if (typeof window !== 'undefined') {
  MetaTagFixer.init();
}

export default MetaTagFixer; 